/**
 * QuizSubQuest Component
 *
 * Handles the full quiz-taking experience for a subquest, including:
 * - Timer management and time-limited quizzes
 * - Navigation between questions
 * - Answer selection and flagging
 * - Quiz submission with confirmation and error handling
 * - Displaying results and questionnaires
 * - Integration with Redux, toast notifications, and auto-play
 *
 * @module components/subQuest/QuizSubQuest
 */

import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

// Chakra UI components
import {
  Box,
  Button,
  Center,
  Divider,
  Flex,
  Icon,
  IconButton,
  Progress,
  Spacer,
  Spinner,
  Text,
  Tooltip,
  useColorModeValue,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { ArrowBackIcon, ArrowForwardIcon } from '@chakra-ui/icons';

// External libraries
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';

// Internal components and hooks
import QuestionItem from 'components/question/QuestionItem';
import { MdFlag } from 'react-icons/md';
import { generateFinalSignature } from 'store/modules/progression/progression-actions';
import ConfirmationModal from 'components/common/modals/ConfirmationModal';
import ErrorModal from 'components/common/modals/ResultModal';
import { useDispatch } from 'react-redux';
import { progressionActions } from 'store/modules/progression/progression-slice';
import useToastNotif from 'hooks/useToastNotif';
import { TOAST_INFO } from 'utils/constants/toast';
import { companionActions } from 'companion/store/companion-slice';
import useSubQuestAutoPlay from 'hooks/useSubQuestAutoPlay';
import QuizStart from './quizzes/QuizStart';
import QuizResult from './quizzes/QuizResult';
import QuizQuestionnaireMd from './quizzes/QuizQuestionnaireMd';

/**
 * QuizSubQuest
 *
 * @param {Object} props - Component props
 * @param {number|null} props.classId - Class ID (nullable)
 * @param {Object} props.course - Course object
 * @param {Object} props.quest - Quest object
 * @param {Object} props.subQuest - SubQuest object (must contain subQuestQuiz)
 * @param {string} props.code - Quiz code
 * @param {Array<Object>} props.quests - List of quests
 * @returns {JSX.Element}
 */
function QuizSubQuest({ classId, course, quest, subQuest, code, quests }) {
  // Modal controls
  const {
    isOpen: isOpenConfirmation,
    onOpen: onOpenConfirmation,
    onClose: onCloseConfirmation,
  } = useDisclosure();

  const {
    isOpen: isOpenError,
    onOpen: onOpenError,
    onClose: onCloseError,
  } = useDisclosure();

  const {
    isOpen: isQuestionnaireMdOpen,
    onOpen: onOpenQuestionnaireMd,
    onClose: OnCloseQuestionnaireMd,
  } = useDisclosure();

  // Quiz configuration
  const { subQuestQuiz } = subQuest;
  const {
    scoreType,
    hasTimeLimit,
    timeLimit,
    totalMark,
    passingScore,
    unlimitedAttempts,
    questions,
  } = subQuestQuiz;

  // State management
  const [position, setPosition] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [time, setTime] = useState(0);
  const [flagQuestions, setFlagQuestions] = useState([]);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [questionnaires, setQuestionnaires] = useState([]);
  const [isStarted, setIsStarted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Redux and hooks
  const dispatch = useDispatch();
  const { generateToastNotif } = useToastNotif();
  dispatch(companionActions.setIsDisabled(true));

  const { triggerAutoPlay } = useSubQuestAutoPlay({
    courseId: course.id,
    questId: quest.id,
    subQuestId: subQuest.id,
    classId,
    quests,
  });

  // Timer effect: countdown if time > 0
  useEffect(() => {
    if (time > 0) {
      const timerId = setInterval(() => {
        setTime((prevTime) => prevTime - 1);
      }, 1000);

      return () => clearInterval(timerId);
    }
    return () => {};
  }, [time]);

  // Show toast notification when time is running out
  useEffect(() => {
    if (isStarted && (time === 30 || time === 60)) {
      generateToastNotif(
        TOAST_INFO,
        'Time is running out',
        `Only ${time} seconds remaining. The quiz will be automatically submitted once time runs out.`,
        true,
        false
      );
    }
  }, [isStarted, time, generateToastNotif]);

  // Navigation handlers
  const handleNextQuestion = () => setPosition((prevPos) => prevPos + 1);
  const handlePrevQuestion = () => setPosition((prevPos) => prevPos - 1);
  const navigateToQuestion = (newPos) => setPosition(newPos);

  // Answer selection handler
  const handleSelectAnswer = (question, values) => {
    setAnswers((prevAnswers) => {
      const existingAnswerIndex = prevAnswers.findIndex(
        (answer) => answer.questionId === question.id
      );
      if (existingAnswerIndex >= 0) {
        const updatedAnswers = [...prevAnswers];
        updatedAnswers[existingAnswerIndex] = {
          questionId: question.id,
          values,
        };
        return updatedAnswers;
      }
      return [...prevAnswers, { questionId: question.id, values }];
    });
  };

  // Find selected answer for a question
  const findSelectedAnswer = (questionId) => {
    const currentAnswer = answers.find(
      (answer) => answer.questionId === questionId
    );
    return currentAnswer ? currentAnswer.values : [];
  };

  // Check if a question is answered
  const isQuestionAnswered = (questionId) => {
    const currentAnswer = answers.find(
      (answer) => answer.questionId === questionId
    );
    if (!currentAnswer) return false;
    return currentAnswer.values.length > 0;
  };

  // Flag/unflag a question
  const handleFlagQuestion = (questionId) => {
    const flagQuestion = flagQuestions.find((fq) => fq === questionId);
    if (flagQuestion) {
      setFlagQuestions((prevFlagQuestions) =>
        prevFlagQuestions.filter((i) => i !== questionId)
      );
    } else {
      setFlagQuestions((prevFlagQuestions) => [
        ...prevFlagQuestions,
        questionId,
      ]);
    }
  };

  // Restart the quiz
  const handleRestart = () => {
    setResult(null);
    setPosition(0);
    setIsStarted(false);
    setAnswers([]);
  };

  // Open questionnaire modal with items
  const handleOpenQuestionnaireMd = (questionnaireItems) => {
    setQuestionnaires(questionnaireItems);
    onOpenQuestionnaireMd();
  };

  // Start the quiz and set timer if needed
  const handleStartQuiz = (timeSecondsLeft) => {
    setIsStarted(true);
    if (timeLimit > 0 && hasTimeLimit && timeSecondsLeft) {
      setTime(timeSecondsLeft);
    }
  };

  // Submit the quiz (async, with error handling)
  const handleSubmitQuiz = useCallback(async () => {
    setIsLoading(true);
    const payload = {
      CourseId: course.id,
      QuestId: quest.id,
      SubQuestId: subQuest.id,
    };

    try {
      const finalSignature = await generateFinalSignature(payload, code);

      payload.Answers = answers;
      if (classId) payload.ClassId = classId;

      const response = await axios.post('/quizzes/finish', payload, {
        headers: {
          'Content-Type': 'application/json',
          'x-signature': finalSignature,
        },
      });

      const { progression } = response.data;

      dispatch(progressionActions.updateProgression(progression));
      setResult(response.data);
      setIsStarted(false);
      onCloseConfirmation();

      if (progression && progression.dateFinished) {
        triggerAutoPlay();
      }
    } catch (e) {
      onOpenError();
      setError({
        status: e.response?.status,
        data: e.response?.data,
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    course.id,
    quest.id,
    subQuest.id,
    classId,
    code,
    answers,
    dispatch,
    onCloseConfirmation,
    onOpenError,
    triggerAutoPlay,
  ]);

  // Auto-submit when time runs out
  useEffect(() => {
    if (time === 0 && isStarted && hasTimeLimit) {
      handleSubmitQuiz();
    }
  }, [time, isStarted, hasTimeLimit, handleSubmitQuiz]);

  // Theming
  const borderColor = useColorModeValue('gray.200', 'teal');
  const buttonColor = useColorModeValue('red', 'teal');

  return (
    <Box
      borderRadius="md"
      boxShadow="sm"
      border="1px"
      borderColor={borderColor}
      h="80vh"
      w="100%"
    >
      {/* Questionnaire Modal */}
      <QuizQuestionnaireMd
        questionnaires={questionnaires}
        isOpen={isQuestionnaireMdOpen}
        onClose={OnCloseQuestionnaireMd}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isVisible={isOpenConfirmation}
        isLoading={isLoading}
        title="Confirmation Required"
        description={`You have answered ${answers.filter((a) => a.values.length > 0).length}/${questions.length} questions. Are you sure you want to submit your quiz answers? Please note that if the quiz has no unlimited attempts, you will not be able to re-take this quiz.`}
        onSubmit={handleSubmitQuiz}
        onCancel={onCloseConfirmation}
      />

      {/* Error Modal */}
      <ErrorModal
        isOpen={isOpenError}
        isError={isOpenError}
        message={error?.data?.title}
        errorObject={error?.data?.errors}
        onSubmit={onCloseError}
      />

      <Flex h="100%" direction="column">
        {/* Header: Quiz Title and Timer */}
        <Flex px={4}>
          <Box py={4}>
            <Text as="b">{subQuest.name}</Text>
          </Box>
          <Spacer />
          {hasTimeLimit && isStarted && (
            <Box my={2} borderRadius="md" px={2}>
              <Flex gap={3}>
                <Center>
                  <Text fontSize="sm" fontWeight={600}>
                    Time Left:
                  </Text>
                </Center>
                <Box bg="grey" borderRadius="md" m={1} px={3} py={1}>
                  <Text color="white" fontSize="sm" fontWeight={500}>
                    {String(Math.floor(time / 60)).padStart(2, '0')}:
                    {String(time % 60).padStart(2, '0')}
                    {isLoading && (
                      <>
                        &nbsp;
                        <Spinner size="sm" />
                      </>
                    )}
                  </Text>
                </Box>
              </Flex>
            </Box>
          )}
        </Flex>

        {/* Progress Bar */}
        <Progress
          colorScheme={useColorModeValue('red', 'teal')}
          size="xs"
          value={answers.filter((a) => a.values.length > 0).length}
          max={questions.length}
        />

        <Box h="100%" p={4} overflowY="scroll">
          {/* Start Quiz */}
          {!isStarted && !result && subQuestQuiz && (
            <QuizStart
              courseId={course.id}
              questId={quest.id}
              subQuestId={subQuest.id}
              hasTimeLimit={hasTimeLimit}
              unlimitedAttempts={unlimitedAttempts}
              timeLimit={timeLimit}
              totalMark={totalMark}
              passingScore={passingScore}
              scoreType={scoreType}
              handleStartQuiz={handleStartQuiz}
              handleOpenQuestionnaireMd={handleOpenQuestionnaireMd}
            />
          )}

          {/* Result Quiz */}
          {!isStarted && result && (
            <QuizResult
              isShowResult={result.isShowResult}
              isShowAnswer={result.isShowAnswer}
              score={result.score}
              totalMark={result.totalMark}
              passingScore={result.passingScore}
              questionnaires={result.questionnaires}
              scoreType={scoreType}
              handleRestart={handleRestart}
              handleOpenQuestionnaireMd={() =>
                handleOpenQuestionnaireMd(result.questionnaires)
              }
            />
          )}

          {/* Question Navigation Buttons */}
          {isStarted && questions && questions.length > 1 && (
            <Center minH="53px">
              <Flex wrap="wrap" gap={2}>
                {questions.map((question, index) => (
                  <Tooltip
                    key={uuidv4()}
                    label={`Navigate to Question No. ${index + 1}`}
                  >
                    <VStack>
                      <Button
                        size="xs"
                        onClick={() => navigateToQuestion(index)}
                        colorScheme={buttonColor}
                        variant={
                          isQuestionAnswered(question.id) ? 'solid' : 'outline'
                        }
                      >
                        {index + 1}
                      </Button>
                      {question.id === questions[position].id && (
                        <Divider
                          borderColor={buttonColor}
                          size={23}
                          variant="solid"
                        />
                      )}
                      {flagQuestions.find((q) => q === question.id) && (
                        <Icon
                          boxSize={3}
                          color={buttonColor}
                          size="xs"
                          as={MdFlag}
                        />
                      )}
                    </VStack>
                  </Tooltip>
                ))}
              </Flex>
            </Center>
          )}

          {/* Current Question */}
          {isStarted && questions[position] && (
            <Box>
              <QuestionItem
                position={position + 1}
                question={questions[position]}
                selectedValue={findSelectedAnswer(questions[position].id)}
                handleSelectAnswer={handleSelectAnswer}
              />
            </Box>
          )}
        </Box>

        {/* Footer: Navigation and Submit */}
        {isStarted && (
          <>
            <Divider />
            <Box px={4} py={2}>
              <Flex align="center">
                <Text fontSize="sm">
                  <Text as="span" fontWeight={500}>
                    {position + 1}
                  </Text>
                  &nbsp;of&nbsp;
                  <Text as="span" fontWeight={500}>
                    {questions.length}
                  </Text>
                  &nbsp;Question{questions.length > 1 && 's'}
                </Text>
                <Spacer />

                <Flex gap={4}>
                  <Tooltip label="Flag this question">
                    <IconButton
                      aria-label="previous"
                      icon={<MdFlag />}
                      onClick={() => {
                        handleFlagQuestion(questions[position].id);
                      }}
                      variant={
                        flagQuestions.find((q) => q === questions[position].id)
                          ? 'solid'
                          : 'outline'
                      }
                      colorScheme={buttonColor}
                      size="md"
                    />
                  </Tooltip>

                  <IconButton
                    aria-label="previous"
                    icon={<ArrowBackIcon />}
                    isDisabled={position === 0}
                    onClick={handlePrevQuestion}
                    variant="outline"
                    colorScheme={buttonColor}
                    size="md"
                  />

                  <IconButton
                    aria-label="next"
                    icon={<ArrowForwardIcon />}
                    isDisabled={position + 1 === questions.length}
                    onClick={handleNextQuestion}
                    variant="outline"
                    colorScheme={buttonColor}
                    size="md"
                  />
                  <Button
                    onClick={onOpenConfirmation}
                    colorScheme={buttonColor}
                  >
                    Submit
                  </Button>
                </Flex>
              </Flex>
            </Box>
          </>
        )}
      </Flex>
    </Box>
  );
}

// Default props for the component
QuizSubQuest.defaultProps = {
  classId: null,
  quests: [],
};

// Prop type validation for maintainability and clarity
QuizSubQuest.propTypes = {
  classId: PropTypes.number,
  course: PropTypes.object.isRequired,
  quest: PropTypes.object.isRequired,
  subQuest: PropTypes.object.isRequired,
  code: PropTypes.string.isRequired,
  quests: PropTypes.array,
};

export default QuizSubQuest;
